import {
  useGetFeatureFlagsQuery,
  useSaveFeatureFlagsMutation,
} from "@/api/feature-flags";
import Button from "@components/ui/button";
import { nanoid } from "@reduxjs/toolkit";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FEATURE_FLAGS_DIALOG_ID } from "../constants";
import {
  addFlag,
  configToEditable,
  editableToConfig,
  removeFlag,
  updateFlag,
  validateEditable,
  type TEditableFlag,
} from "../feature-flags-editor.utils";
import { FlagRow } from "./flag-row";

const FeatureFlagsEditor = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading, isError } = useGetFeatureFlagsQuery();
  const [flags, setFlags] = useState<TEditableFlag[]>([]);
  const [version, setVersion] = useState(0);
  const [saveFeatureFlags, { isLoading: isSaving }] =
    useSaveFeatureFlagsMutation();

  useEffect(() => {
    if (!data) return;
    setFlags(configToEditable(data));
    setVersion(data.version);
  }, [data]);

  const loadError = isError
    ? "Could not load current flags. Saving is disabled to avoid overwriting live flags - close and reopen to retry."
    : null;

  const close = () => dispatch(closeDialog(FEATURE_FLAGS_DIALOG_ID));

  const handleSave = async () => {
    if (loadError) return;
    const validation = validateEditable(flags);
    if (!validation.ok) {
      toast.error(validation.error);
      return;
    }
    try {
      await saveFeatureFlags(editableToConfig(flags, version)).unwrap();
      toast.success(
        "Saved - changes take effect on the next editor load (~1 min).",
      );
      close();
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        "Failed to publish feature flags.";
      toast.error(message);
    }
  };

  return (
    <div className="tw-flex tw-w-[720px] tw-max-w-[90vw] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-blue/5 tw-bg-white tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-b-blue/5 tw-px-6 tw-py-4">
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <h2 className="tw-m-0 tw-font-sans tw-text-base tw-font-semibold tw-leading-6 tw-text-blue">
            Feature Flags
          </h2>
          <p className="tw-m-0 tw-font-sans tw-text-xs tw-leading-4 tw-text-blue-500">
            Published to the CDN. Allowlisted orgs are always on; rollout %
            buckets by org.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={close}
          className="tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-blue-500 tw-transition-colors hover:tw-bg-blue-50 hover:tw-text-blue"
        >
          <IconX className="tw-size-4" />
        </button>
      </div>

      <div className="scrollbar tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-6 tw-py-6">
        {isLoading && (
          <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-500">
            Loading current flags…
          </p>
        )}
        {loadError && (
          <p className="tw-bg-orange-50 tw-text-orange-700 tw-m-0 tw-rounded-md tw-px-3 tw-py-2 tw-font-sans tw-text-sm">
            {loadError}
          </p>
        )}
        {!isLoading &&
          flags.map((flag) => (
            <FlagRow
              key={flag.id}
              flag={flag}
              onChange={(patch) =>
                setFlags((prev) => updateFlag(prev, flag.id, patch))
              }
              onRemove={() => setFlags((prev) => removeFlag(prev, flag.id))}
            />
          ))}
        {!isLoading && flags.length === 0 && (
          <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-400">
            No flags yet.
          </p>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="tw-shrink-0 tw-self-start"
          onClick={() => setFlags((prev) => addFlag(prev, nanoid()))}
        >
          <IconPlus className="tw-size-4" />
          Add flag
        </Button>
      </div>

      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-t-blue/5 tw-px-6 tw-py-3">
        <Button variant="ghost" size="sm" onClick={close} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={isLoading || isSaving || !!loadError}
          isLoading={isSaving}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default FeatureFlagsEditor;
